import { toast } from "react-toastify";

const AUTH_KEYS = [
  "token",
  "role",
  "team",
  "emp_id",
  "designation",
  "designation_name",
  "loginTime",
  "user",
];

const EPHEMERAL_AUTH_KEYS = ["tempLoginToken", "tempEmpId"];

const getSessionStore = () =>
  typeof window !== "undefined" ? window.sessionStorage : null;

const getLocalStore = () =>
  typeof window !== "undefined" ? window.localStorage : null;

const AUTH_SESSION_UPDATED_EVENT = "auth-session-updated";
const SESSION_DURATION_MS = 8 * 60 * 60 * 1000;
const SESSION_REMINDER_MINUTES = [5, 2, 1];
const sessionReminderTimers = new Map();
let sessionLogoutTimer = null;

const emitAuthSessionUpdated = () => {
  if (typeof window === "undefined") return;
  window.dispatchEvent(new Event(AUTH_SESSION_UPDATED_EVENT));
};

const dismissSessionReminderToasts = () => {
  SESSION_REMINDER_MINUTES.forEach((minutes) => {
    toast.dismiss(`session-expiry-${minutes}`);
  });
};

const clearSessionReminderTimers = () => {
  sessionReminderTimers.forEach((timerId) => window.clearTimeout(timerId));
  sessionReminderTimers.clear();

  if (sessionLogoutTimer) {
    window.clearTimeout(sessionLogoutTimer);
    sessionLogoutTimer = null;
  }
};

const getLoginTimestamp = () => {
  const loginTime = Number(getAuthItem("loginTime"));
  return Number.isFinite(loginTime) && loginTime > 0 ? loginTime : null;
};

const getSessionRemainingMs = () => {
  const loginTimestamp = getLoginTimestamp();
  if (!loginTimestamp) return null;
  return loginTimestamp + SESSION_DURATION_MS - Date.now();
};

const showSessionReminder = (minutes) => {
  toast.warning(
    `You will be logged out automatically in ${minutes} minute${minutes === 1 ? "" : "s"}.`,
    {
      toastId: `session-expiry-${minutes}`,
      autoClose: 10000,
    }
  );
};

export const syncSessionTimeouts = () => {
  if (typeof window === "undefined") return;

  clearSessionReminderTimers();
  dismissSessionReminderToasts();

  if (!getAuthItem("token")) return;

  const remainingMs = getSessionRemainingMs();
  if (remainingMs === null) return;

  if (remainingMs <= 0) {
    logout();
    return;
  }

  if (remainingMs <= 60 * 1000) {
    showSessionReminder(1);
  } else if (remainingMs <= 2 * 60 * 1000) {
    showSessionReminder(2);
  } else if (remainingMs <= 5 * 60 * 1000) {
    showSessionReminder(5);
  }

  SESSION_REMINDER_MINUTES.forEach((minutes) => {
    const triggerInMs = remainingMs - minutes * 60 * 1000;
    if (triggerInMs <= 0) return;

    const timerId = window.setTimeout(() => {
      showSessionReminder(minutes);
    }, triggerInMs);

    sessionReminderTimers.set(minutes, timerId);
  });

  sessionLogoutTimer = window.setTimeout(() => {
    logout();
  }, remainingMs);
};

export const getAuthItem = (key) =>
  getSessionStore()?.getItem(key) || getLocalStore()?.getItem(key) || null;

export const setAuthSession = (entries = {}) => {
  const sessionStore = getSessionStore();
  const localStore = getLocalStore();
  if (!sessionStore && !localStore) return;

  Object.entries(entries).forEach(([key, value]) => {
    if (value === undefined || value === null) {
      sessionStore?.removeItem(key);
      localStore?.removeItem(key);
      return;
    }

    sessionStore?.setItem(key, String(value));
    localStore?.setItem(key, String(value));
  });

  emitAuthSessionUpdated();
};

export const applyAuthLoginPayload = (payload = {}) => {
  const teamName = payload.team || payload.team_name || "";
  const designationName = payload.designation_name || payload.designation || "";

  setAuthSession({
    token: payload.token,
    role: payload.role,
    team: teamName,
    emp_id: payload.emp_id,
    designation: designationName,
    designation_name: designationName,
    loginTime: Date.now(),
    user: JSON.stringify({
      emp_id: payload.emp_id,
      role: payload.role?.toString().trim().toUpperCase() || "",
      name: payload.name || "",
      team: teamName,
      team_name: payload.team_name || teamName,
      department: payload.department || payload.team || payload.team_name || "",
      designation: payload.designation || designationName,
      designation_name: designationName,
      profile_photo: payload.profile_photo || null,
      two_factor_enabled: Boolean(payload.two_factor_enabled),
    }),
  });

  EPHEMERAL_AUTH_KEYS.forEach((key) => getSessionStore()?.removeItem(key));
};

export const getAuthUser = () => {
  try {
    return JSON.parse(getAuthItem("user") || "null");
  } catch {
    return null;
  }
};

export const clearLegacyAuthStorage = ({ force = false } = {}) => {
  const sessionStore = getSessionStore();
  const localStore = getLocalStore();
  if (!localStore) return;

  AUTH_KEYS.forEach((key) => {
    if (force || sessionStore?.getItem(key)) {
      localStore.removeItem(key);
    }
  });
};

export const clearAuthSession = () => {
  clearSessionReminderTimers();
  dismissSessionReminderToasts();

  const sessionStore = getSessionStore();
  AUTH_KEYS.forEach((key) => {
    sessionStore?.removeItem(key);
  });
  EPHEMERAL_AUTH_KEYS.forEach((key) => {
    sessionStore?.removeItem(key);
  });

  clearLegacyAuthStorage({ force: true });
  emitAuthSessionUpdated();
};

export const updateAuthUser = (updater) => {
  const currentUser = getAuthUser() || {};
  const nextUser =
    typeof updater === "function" ? updater(currentUser) : { ...currentUser, ...updater };

  setAuthSession({
    user: JSON.stringify(nextUser),
    emp_id: nextUser.emp_id ?? getAuthItem("emp_id"),
    role: nextUser.role ?? getAuthItem("role"),
    team: nextUser.team_name || nextUser.team || getAuthItem("team"),
    designation:
      nextUser.designation_name || nextUser.designation || getAuthItem("designation"),
    designation_name:
      nextUser.designation_name || nextUser.designation || getAuthItem("designation_name"),
  });
};

export const subscribeToAuthSession = (listener) => {
  if (typeof window === "undefined") return () => {};

  window.addEventListener(AUTH_SESSION_UPDATED_EVENT, listener);
  return () => window.removeEventListener(AUTH_SESSION_UPDATED_EVENT, listener);
};

export const isAuthenticated = () => !!getAuthItem("token");

export const checkSession = () => {
  const remainingMs = getSessionRemainingMs();
  if (remainingMs !== null && remainingMs <= 0) {
    logout();
  }
};

export const logout = () => {
  clearAuthSession();
  window.location.href = "/login";
};
